import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { OfferPageActions, OfferPageMultiRoomAutoselectionReason } from 'contexts/OfferPage/offerPageStateReducer'
import { useAppSelector } from 'hooks/reduxHooks'
import { useContext, useEffect } from 'react'
import { getActivePackagesByRoom } from 'selectors/offerPage/bedbankMultipleRoomBookingSelectors'
import useOfferPageDispatch from './useOfferPageDispatch'
import { EmptyObject } from 'lib/object/objectUtils'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'

export default function useBedbankIndividualRoomAutoselection() {
  const offerPageState = useContext(OfferPageStateContext)
  const { checkInDate, checkOutDate, rooms, bedbankOffer } = offerPageState

  const dispatch = useOfferPageDispatch()
  const rates = useAppSelector(appState => appState.offer.bedbankOfferRates[bedbankOffer?.id!]) ?? EmptyObject

  const isMultiRoomUIActive = useAppSelector(appState => isMultipleRoomBookingActive(offerPageState, appState))

  const areAnyRatesLoading = !!(checkInDate && checkOutDate && rooms.some(room => !rates[getBedbankRateKey([room], checkInDate, checkOutDate)]))

  const selectedPackagesByRoom = useAppSelector(appState => getActivePackagesByRoom(offerPageState, appState))

  useEffect(() => {
    if (!isMultiRoomUIActive || areAnyRatesLoading) {
      return
    }

    const anySelectionsDiffer = rooms.some(room => {
      const selection = selectedPackagesByRoom[room.id]
      return room.roomId !== selection?.package.roomId || room.rateId !== selection?.rate?.id
    })

    if (!anySelectionsDiffer) {
      return
    }

    const allSelections = Object.values(selectedPackagesByRoom)
    let autoselectionReason: OfferPageMultiRoomAutoselectionReason | undefined = undefined
    if (allSelections.some(selection => selection?.selectionType === 'wasUnavailable')) {
      autoselectionReason = 'invalid-selections'
    } else {
      autoselectionReason = 'initial-selection'
    }

    const newRooms = rooms.map(room => {
      const selection = selectedPackagesByRoom[room.id]
      return {
        ...room,
        roomId: selection?.package.roomId,
        rateId: selection?.rate?.id,
      }
    })

    dispatch({ type: OfferPageActions.setRooms, rooms: newRooms })
    dispatch({ type: OfferPageActions.setMultiRoomAutoselectionReason, reason: autoselectionReason })
  }, [dispatch, isMultiRoomUIActive, rooms, selectedPackagesByRoom, areAnyRatesLoading])
}
